import type { PoolCapacityView } from '@/shared/api/__generated__'

// Квант железа: ячейка 1C + 2L несёт ровно столько одновременных звонков и
// между заявками не делится. Картина ёмкости отдаёт своё `cell_slots` — это
// дефолт для формы, пока картина не доехала.
export const CELL_SLOTS = 4

/**
 * Проверка размера заявки до отправки. Кратность четырём — не округление ради
 * удобства: заявка на 6 слотов подняла бы две ячейки и оплачивала бы 8,
 * поэтому бэк её не принимает вовсе.
 */
export function reservationSlotsError(
  raw: string,
  quantum: number = CELL_SLOTS,
  sellable?: number,
): string | null {
  const trimmed = raw.trim()
  if (trimmed === '') return 'Сколько одновременных звонков нужно заявке'
  const slots = Number(trimmed)
  if (!Number.isInteger(slots)) return 'Слоты — целое число'
  if (slots < quantum) return `Минимум ${quantum}: меньше кванта ячейку не поднять`
  if (slots % quantum !== 0) {
    return `Кратно ${quantum}: ячейка несёт ${quantum} звонка и между заявками не делится`
  }
  if (sellable !== undefined && sellable > 0 && slots > sellable) {
    return `Пул продаёт максимум ${sellable} — линий на большее у него нет`
  }
  return null
}

export interface PoolLinesDrift {
  // Каналы, за которые оператор платит, а ядро их поднять не может: учётка
  // обслуживает одну ячейку и отдаёт ей максимум CELL_SLOTS линий.
  wastedChannels: number
  // Поднимаемые линии сверх кратности кванта — продать их нельзя никогда.
  unsellableLines: number
  // Продаваемые слоты, ещё не обещанные ни одной заявке.
  unbookedSlots: number
}

export function poolLinesDrift(pool: PoolCapacityView): PoolLinesDrift {
  return {
    wastedChannels: Math.max(0, pool.lines_capacity - pool.usable_lines),
    unsellableLines: Math.max(0, pool.usable_lines - pool.sellable_slots),
    unbookedSlots: Math.max(0, pool.sellable_slots - pool.reserved_slots),
  }
}

export type PoolPromise = 'fits' | 'queued' | 'shrunk'

/**
 * Обещано ли пулом больше, чем он продаёт, и почему. Превышение само по себе
 * не авария: пока просроченный сосед держит линии, его слоты и слоты ждущей
 * заявки считаются оба — это очередь, а не переобещание. Аварией оно
 * становится, когда ждущих нет: значит ёмкость просела уже после приёма
 * заявок (учётку выключили, каналы урезали).
 */
export function poolPromise(pool: PoolCapacityView): PoolPromise {
  if (pool.reserved_slots <= pool.sellable_slots) return 'fits'
  return pool.waiting_slots > 0 ? 'queued' : 'shrunk'
}

/**
 * Честный слот ячейки — `min(слоты железа, арендованные линии)`. Купленная
 * карта без линии не принимает ни одного звонка, поэтому ёмкостью считается
 * меньшее из двух, а не то, за что заплачено.
 */
export function honestSlots(slotsTotal: number, leasedLines: number): number {
  return Math.max(0, Math.min(slotsTotal, leasedLines))
}

export interface SlotsHonesty {
  /** План control plane: слоты железа ячейки. */
  plan: number
  /** Линии пула, реально арендованные C-юнитом ячейки. */
  leasedLines: number
  /** Факт: сколько звонков ячейка физически примет. */
  fact: number
  /** Разрыв плана с фактом — оплаченные слоты, которым не на чем звонить. */
  gap: number
}

export function slotsHonesty(slotsTotal: number, leasedLines: number): SlotsHonesty {
  const fact = honestSlots(slotsTotal, leasedLines)
  return {
    plan: Math.max(0, slotsTotal),
    leasedLines: Math.max(0, leasedLines),
    fact,
    gap: Math.max(0, slotsTotal - fact),
  }
}

export interface CellSlots {
  cellId: string
  slotsTotal: number
  leasedLines: number
}

export interface ReservationHonesty {
  rows: Array<SlotsHonesty & { cellId: string }>
  plan: number
  fact: number
  gap: number
}

/**
 * Честные слоты заявки по её ячейкам. Разрыв плана с фактом — единственный
 * признак, по которому «линии ушли из-под живой заявки» видно в админке:
 * причину `lines-shrunk` HTTP-поверхность не отдаёт нигде.
 */
export function reservationHonesty(cells: CellSlots[]): ReservationHonesty {
  const rows = cells.map((c) => ({
    cellId: c.cellId,
    ...slotsHonesty(c.slotsTotal, c.leasedLines),
  }))
  const plan = rows.reduce((acc, r) => acc + r.plan, 0)
  const fact = rows.reduce((acc, r) => acc + r.fact, 0)
  return { rows, plan, fact, gap: Math.max(0, plan - fact) }
}
